/**
 * UI-6a Task 8 — ScheduledReportDrawer.
 *
 * Create/edit drawer for a scheduled report config, hosted inside the shared
 * `ResourceDrawer` with `width="wide"`. In edit mode the body splits into a
 * two-pane layout: form on the left, RunHistoryPane on the right.
 *
 * Backend contract (apps/api/src/routes/reports/scheduled-configs.ts):
 *   - POST  /reports/scheduled-configs           — create
 *   - PATCH /reports/scheduled-configs/:id       — update
 *
 * Body shape (per CreateConfigSchema):
 *   {
 *     reportType: 'call-volume' | 'agent-performance' | 'sla' | 'csat'
 *     recipientEmails: string[]                  // non-empty
 *     format: 'pdf' | 'excel' | 'csv'
 *     cronExpression: string                     // 5- or 6-field cron
 *     lookbackDays: number                       // 1-365
 *     enabled: boolean
 *   }
 *
 * Note: the shared `ScheduleEditor` is a structured editor accepting a
 * `Schedule` discriminated union (modes: 'cron' | 'interval' | 'once').
 * Only the cron mode maps to the backend's `cronExpression` string. The
 * drawer pins the editor to cron mode and converts between
 * `Schedule {mode:'cron'}` and the raw cron string at the form boundary.
 *
 * Save ownership matches `SipTrunkDrawer`: props are `{ open, config, onClose,
 * onSaved }` (no `onSave`); the drawer performs the POST/PATCH itself, then
 * fires `onSaved()` + `onClose()`. Errors surface via toast and the drawer
 * stays open.
 */
import { useRef, useState, type JSX } from 'react'
import { z } from 'zod'
import { Controller, useFormContext } from 'react-hook-form'
import ResourceDrawer from '../ResourceDrawer.js'
import ScheduleEditor, { type Schedule } from '../ScheduleEditor.js'
import RecipientsChipsInput from './RecipientsChipsInput.js'
import RunHistoryPane from './RunHistoryPane.js'
import {
  ZodForm,
  FormDropdown,
  FormNumber,
  FormSelectButton,
  FormCheckbox,
} from '../../forms/index.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import type { ScheduledReportConfig } from '../../../hooks/useScheduledReports.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface ScheduledReportDrawerProps {
  open: boolean
  /** The config being edited; `null` = create mode. */
  config: ScheduledReportConfig | null
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Options + constants
// ---------------------------------------------------------------------------

const REPORT_TYPE_OPTIONS = [
  { label: 'Call Volume', value: 'call-volume' },
  { label: 'Agent Performance', value: 'agent-performance' },
  { label: 'SLA', value: 'sla' },
  { label: 'CSAT', value: 'csat' },
] as const

const FORMAT_OPTIONS = [
  { label: 'CSV', value: 'csv' },
  { label: 'Excel', value: 'excel' },
  { label: 'PDF', value: 'pdf' },
] as const

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const ScheduledReportSchema = z.object({
  reportType: z.enum(['call-volume', 'agent-performance', 'sla', 'csat'], {
    message: 'Report type is required',
  }),
  recipientEmails: z.array(z.string()).min(1, 'Add at least one recipient'),
  format: z.enum(['pdf', 'excel', 'csv']),
  cronExpression: z
    .string()
    .trim()
    .min(1, 'Cron expression is required'),
  lookbackDays: z
    .number({ message: 'Lookback days is required' })
    .int('Must be an integer')
    .min(1, 'Must be ≥ 1')
    .max(365, 'Must be ≤ 365'),
  enabled: z.boolean(),
})

type FormValues = z.input<typeof ScheduledReportSchema>

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ScheduledReportDrawer({
  open,
  config,
  onClose,
  onSaved,
}: ScheduledReportDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const [saving, setSaving] = useState(false)

  const isEdit = config !== null
  /** Remount key — changes when a different config (or create mode) opens. */
  const contentKey = config?.id ?? 'new'

  // --- Default values ------------------------------------------------------

  const defaultValues: FormValues = config
    ? {
        reportType: config.reportType as FormValues['reportType'],
        recipientEmails: config.recipientEmails,
        format: config.format as FormValues['format'],
        cronExpression: config.cronExpression,
        lookbackDays: config.lookbackDays,
        enabled: config.enabled,
      }
    : {
        reportType: 'call-volume',
        recipientEmails: [],
        format: 'csv',
        cronExpression: '',
        lookbackDays: 7,
        enabled: true,
      }

  // --- Save flow -----------------------------------------------------------

  async function handleSubmit(values: FormValues): Promise<void> {
    setSaving(true)
    try {
      const body = {
        reportType: values.reportType,
        recipientEmails: values.recipientEmails,
        format: values.format,
        cronExpression: values.cronExpression.trim(),
        lookbackDays: values.lookbackDays,
        enabled: values.enabled,
      }
      if (config) {
        await api.patch(`/reports/scheduled-configs/${config.id}`, body)
        toast.success('Scheduled report updated')
      } else {
        await api.post('/reports/scheduled-configs', body)
        toast.success('Scheduled report created')
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to save scheduled report.',
      )
    } finally {
      setSaving(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      formRef={formRef}
      saving={saving}
      width="wide"
      title={config ? 'Edit Scheduled Report' : 'New Scheduled Report'}
    >
      <div className={isEdit ? 'grid grid-cols-2 gap-6' : ''}>
        <div>
          <ZodForm
            key={contentKey}
            schema={ScheduledReportSchema}
            defaultValues={defaultValues}
            onSubmit={(data) => handleSubmit(data as FormValues)}
            formRef={formRef}
          >
            <FormDropdown
              name="reportType"
              label="Report Type"
              required
              options={[...REPORT_TYPE_OPTIONS]}
            />

            <CronField />

            <RecipientsField />

            <FormSelectButton
              name="format"
              label="Format"
              options={[...FORMAT_OPTIONS]}
            />

            <FormNumber
              name="lookbackDays"
              label="Lookback Days"
              min={1}
              max={365}
              required
              helpText="How many days of data to include (1-365)."
            />

            <FormCheckbox name="enabled" label="Enabled" />
          </ZodForm>
        </div>

        {isEdit && config ? (
          <div>
            <RunHistoryPane configId={config.id} />
          </div>
        ) : null}
      </div>
    </ResourceDrawer>
  )
}

// ---------------------------------------------------------------------------
// Field adapters — wrap the structured (non-context-aware) editors in
// react-hook-form `Controller`s so they participate in ZodForm validation.
// ---------------------------------------------------------------------------

/**
 * Pin the shared `ScheduleEditor` to cron mode for the scheduled-reports
 * flow. The editor accepts a `Schedule` union but our backend stores only
 * `cronExpression: string` — so we marshal the string into/out of
 * `{ mode: 'cron', expression }` at the form boundary.
 */
function CronField(): JSX.Element {
  const { control, formState: { errors } } = useFormContext<FormValues>()
  const error = errors.cronExpression
  const errorMessage =
    typeof error?.message === 'string' ? error.message : undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      <span className="text-sm font-medium">
        Schedule<span aria-hidden> *</span>
      </span>
      <Controller
        name="cronExpression"
        control={control}
        render={({ field }) => {
          const value: Schedule = { mode: 'cron', expression: field.value }
          return (
            <ScheduleEditor
              value={value}
              onChange={(next) => {
                if (next && next.mode === 'cron') {
                  field.onChange(next.expression)
                } else if (next === null) {
                  field.onChange('')
                }
                // Other modes are not surfaced for this form. The editor's
                // mode <select> would let the user switch, but we expect the
                // cron mode to be the de-facto editor for scheduled reports.
              }}
              nextRunPreview
            />
          )
        }}
      />
      {errorMessage && (
        <small role="alert" className="text-red-600">
          {errorMessage}
        </small>
      )}
    </div>
  )
}

/**
 * Adapts `RecipientsChipsInput` (a controlled value/onChange pair) to a
 * react-hook-form field via `Controller`.
 */
function RecipientsField(): JSX.Element {
  const { control, formState: { errors } } = useFormContext<FormValues>()
  const error = errors.recipientEmails
  const errorMessage =
    typeof error?.message === 'string' ? error.message : undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      <span className="text-sm font-medium">
        Recipients<span aria-hidden> *</span>
      </span>
      <Controller
        name="recipientEmails"
        control={control}
        render={({ field }) => (
          <RecipientsChipsInput
            value={field.value ?? []}
            onChange={(next) => field.onChange(next)}
          />
        )}
      />
      {errorMessage && (
        <small role="alert" className="text-red-600">
          {errorMessage}
        </small>
      )}
    </div>
  )
}
